import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Quote } from "lucide-react";
import { api, API } from "@/lib/api";
import { Section, SHADES } from "@/components/site/ServiceBlocks";

export const photoUrl = (id) => `${API}/testimonials/photo/${id}`;

export function Card({ t, i }) {
  const c = SHADES[i % SHADES.length];
  const initials = t.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <figure data-testid={`testimonial-${i}`} className="h-full rounded-lg border bg-[#0F172A] p-6 flex flex-col gap-5 transition-transform duration-300 hover:-translate-y-1" style={{ borderColor: `${c}55` }}>
      <Quote className="h-7 w-7" style={{ color: c }} />
      <blockquote className="text-slate-200 leading-relaxed flex-1">“{t.quote}”</blockquote>
      <figcaption className="flex items-center gap-3 pt-4 border-t border-slate-800">
        {t.photo_id ? <img src={photoUrl(t.photo_id)} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
          : <span className="h-12 w-12 rounded-full grid place-items-center font-bold text-slate-950" style={{ background: c }}>{initials}</span>}
        <div>
          <p className="font-semibold">{t.name}</p>
          <p className="text-xs text-slate-400">{[t.job_title, t.company].filter(Boolean).join(", ")}</p>
        </div>
      </figcaption>
    </figure>
  );
}

export function Testimonials({ page }) {
  const { data = [] } = useQ(page);
  if (!data.length) return null;
  return (
    <Section tint eyebrow="Testimonials" title="What our clients say">
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="testimonials">
        {data.map((t, i) => <Card key={t.id} t={t} i={i} />)}
      </div>
    </Section>
  );
}

const useQ = (page) => useQuery({ queryKey: ["testimonials", page], queryFn: async () => (await api.get("/testimonials", { params: { page } })).data });

function usePerView() {
  const get = () => (window.innerWidth >= 1280 ? 3 : window.innerWidth >= 768 ? 2 : 1);
  const [n, setN] = useState(get);
  useEffect(() => { const f = () => setN(get()); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return n;
}

export function TestimonialCarousel({ page }) {
  const { data = [] } = useQ(page);
  const per = usePerView();
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const steps = Math.max(data.length - per + 1, 1);
  useEffect(() => {
    if (paused || steps < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % steps), 6500);
    return () => clearInterval(t);
  }, [paused, steps]);
  useEffect(() => { if (i >= steps) setI(0); }, [i, steps]);
  if (!data.length) return null;
  return (
    <Section tint eyebrow="Testimonials" title="What our clients say">
      <div className="overflow-hidden" data-testid="testimonials" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${(i * 100) / per}%)` }} data-testid="testimonial-track">
          {data.map((t, k) => <div key={t.id} className="shrink-0 px-2.5" style={{ width: `${100 / per}%` }}><Card t={t} i={k} /></div>)}
        </div>
      </div>
      {steps > 1 && (
        <div className="flex justify-center gap-2 mt-8" data-testid="testimonial-dots">
          {Array.from({ length: steps }).map((_, k) => (
            <button key={k} onClick={() => setI(k)} data-testid={`testimonial-dot-${k}`} aria-label={`Testimonials ${k + 1}`}
              className={`h-1.5 rounded-full transition-all ${k === i ? "w-10 bg-[#F58B23]" : "w-5 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
      )}
    </Section>
  );
}
